<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import WhatsappCampaignsAPI from 'dashboard/api/whatsappCampaigns';

const props = defineProps({
  inboxId: { type: [String, Number], default: '' },
  inboxes: { type: Array, default: () => [] },
  // 'create' | 'view' | 'edit'
  mode: { type: String, default: 'create' },
  template: { type: Object, default: null },
});

const emit = defineEmits(['close', 'created', 'saved']);
const submitting = ref(false);
const uploadingMedia = ref(false);
const mediaFileInput = ref(null);
const uploadedMediaPreview = ref(null);

// ─── mode handling ───────────────────────────────────────────────────────────────
const currentMode = ref(props.mode);
const isCreate = computed(() => currentMode.value === 'create');
const isView = computed(() => currentMode.value === 'view');
const isEdit = computed(() => currentMode.value === 'edit');
const readOnly = computed(() => isView.value);
// Meta forbids changing name/language after creation; category locked once APPROVED.
const nameLocked = computed(() => !isCreate.value);
const templateStatus = computed(() =>
  (props.template?.status || '').toLowerCase()
);
const categoryLocked = computed(
  () => isEdit.value && templateStatus.value === 'approved'
);

// ─── form state ────────────────────────────────────────────────────────────────
const selectedInboxId = ref(props.inboxId || '');

const form = reactive({
  name: '',
  language: 'pt_BR',
  category: 'MARKETING',
  header: { enabled: false, type: 'TEXT', text: '', url: '', example: '' },
  body: { text: '', examples: {} },
  footer: { enabled: false, text: '' },
  buttons: [],
});

// ─── languages ─────────────────────────────────────────────────────────────────
const LANGUAGES = [
  { value: 'pt_BR', label: 'Português (Brasil)' },
  { value: 'pt_PT', label: 'Português (Portugal)' },
  { value: 'en_US', label: 'English (US)' },
  { value: 'es', label: 'Español' },
  { value: 'es_ES', label: 'Español (España)' },
  { value: 'fr', label: 'Français' },
  { value: 'de', label: 'Deutsch' },
  { value: 'it', label: 'Italiano' },
  { value: 'ja', label: '日本語' },
  { value: 'ko', label: '한국어' },
  { value: 'zh_CN', label: '中文 (简体)' },
  { value: 'ar', label: 'العربية' },
];

const BUTTON_TYPES = [
  { value: 'QUICK_REPLY', label: 'Resposta Rápida' },
  { value: 'URL', label: 'URL' },
  { value: 'PHONE_NUMBER', label: 'Telefone' },
];

// ─── variable extraction ────────────────────────────────────────────────────────
const extractVars = text => {
  const nums = new Set();
  for (const m of text.matchAll(/\{\{(\d+)\}\}/g)) nums.add(parseInt(m[1]));
  return [...nums].sort((a, b) => a - b);
};

const bodyVars = computed(() => extractVars(form.body.text));
const headerHasVar = computed(() => /\{\{1\}\}/.test(form.header.text));

watch(bodyVars, vars => {
  const cleaned = {};
  vars.forEach(n => {
    cleaned[n] = form.body.examples[n] || '';
  });
  form.body.examples = cleaned;
});

// ─── button helpers ─────────────────────────────────────────────────────────────
const addButton = () => {
  if (form.buttons.length >= 3) return;
  form.buttons.push({
    type: 'QUICK_REPLY',
    text: '',
    url: '',
    phone_number: '',
    urlExample: '',
  });
};

const removeButton = idx => form.buttons.splice(idx, 1);

const buttonHasUrlVar = btn => btn.type === 'URL' && /\{\{1\}\}/.test(btn.url);

// ─── preview rendering ──────────────────────────────────────────────────────────
const previewBody = computed(() =>
  form.body.text.replace(
    /\{\{(\d+)\}\}/g,
    (_, n) => form.body.examples[n] || `{{${n}}}`
  )
);

const previewHeaderText = computed(() =>
  form.header.text.replace(/\{\{1\}\}/g, form.header.example || '{{1}}')
);

const renderBold = text =>
  text
    .replace(/\*([^*]+)\*/g, '<strong>$1</strong>')
    .replace(/_([^_]+)_/g, '<em>$1</em>')
    .replace(/~([^~]+)~/g, '<s>$1</s>')
    .replace(/\n/g, '<br>');

// ─── build Meta API components ──────────────────────────────────────────────────
const buildComponents = () => {
  const components = [];

  // HEADER
  if (form.header.enabled) {
    if (form.header.type === 'TEXT' && form.header.text) {
      const comp = { type: 'HEADER', format: 'TEXT', text: form.header.text };
      if (headerHasVar.value && form.header.example)
        comp.example = { header_text: [form.header.example] };
      components.push(comp);
    } else if (['IMAGE', 'VIDEO', 'DOCUMENT'].includes(form.header.type)) {
      const comp = { type: 'HEADER', format: form.header.type };
      if (form.header.url) comp.example = { header_handle: [form.header.url] };
      components.push(comp);
    }
  }

  // BODY
  if (form.body.text) {
    const comp = { type: 'BODY', text: form.body.text };
    if (bodyVars.value.length > 0) {
      const exampleVals = bodyVars.value.map(n => form.body.examples[n] || '');
      comp.example = { body_text: [exampleVals] };
    }
    components.push(comp);
  }

  // FOOTER
  if (form.footer.enabled && form.footer.text)
    components.push({ type: 'FOOTER', text: form.footer.text });

  // BUTTONS
  if (form.buttons.length > 0) {
    const buttons = form.buttons
      .map(btn => {
        if (btn.type === 'QUICK_REPLY')
          return { type: 'QUICK_REPLY', text: btn.text };
        if (btn.type === 'URL') {
          const b = { type: 'URL', text: btn.text, url: btn.url };
          if (buttonHasUrlVar(btn) && btn.urlExample)
            b.example = [btn.urlExample];
          return b;
        }
        if (btn.type === 'PHONE_NUMBER')
          return {
            type: 'PHONE_NUMBER',
            text: btn.text,
            phone_number: btn.phone_number,
          };
        return null;
      })
      .filter(Boolean);
    if (buttons.length) components.push({ type: 'BUTTONS', buttons });
  }

  return components;
};

// ─── hydrate form from an existing template (reverse of buildComponents) ─────────
const SUPPORTED_HEADER_FORMATS = ['TEXT', 'IMAGE', 'VIDEO', 'DOCUMENT'];
const SUPPORTED_BUTTON_TYPES = ['QUICK_REPLY', 'URL', 'PHONE_NUMBER'];

const isUnsupportedComponents = components => {
  let headerCount = 0;
  for (const c of components || []) {
    const type = (c.type || '').toUpperCase();
    if (type === 'HEADER') {
      headerCount += 1;
      if (headerCount > 1) return true;
      if (
        !SUPPORTED_HEADER_FORMATS.includes((c.format || 'TEXT').toUpperCase())
      )
        return true;
    } else if (type === 'BUTTONS') {
      for (const b of c.buttons || []) {
        if (!SUPPORTED_BUTTON_TYPES.includes((b.type || '').toUpperCase()))
          return true;
      }
    } else if (type !== 'BODY' && type !== 'FOOTER') {
      // CAROUSEL, LIMITED_TIME_OFFER, etc. — not modelled by this builder
      return true;
    }
  }
  return false;
};

const hydrateFromTemplate = tpl => {
  if (!tpl) return;
  form.name = tpl.name || '';
  form.language = tpl.language || 'pt_BR';
  form.category = (tpl.category || 'MARKETING').toUpperCase();
  form.header = {
    enabled: false,
    type: 'TEXT',
    text: '',
    url: '',
    example: '',
  };
  form.footer = { enabled: false, text: '' };
  form.buttons = [];
  form.body = { text: '', examples: {} };

  const comps = tpl.components || [];
  const findComp = type =>
    comps.find(c => (c.type || '').toUpperCase() === type);

  const bodyComp = findComp('BODY');
  if (bodyComp) {
    form.body.text = bodyComp.text || '';
    const exampleVals = bodyComp.example?.body_text?.[0] || [];
    const examples = {};
    extractVars(form.body.text).forEach((n, i) => {
      examples[n] = exampleVals[i] ?? '';
    });
    form.body.examples = examples;
  }

  const headerComp = findComp('HEADER');
  if (headerComp) {
    const fmt = (headerComp.format || 'TEXT').toUpperCase();
    form.header.enabled = true;
    form.header.type = fmt;
    if (fmt === 'TEXT') {
      form.header.text = headerComp.text || '';
      form.header.example = headerComp.example?.header_text?.[0] || '';
    } else {
      form.header.url = headerComp.example?.header_handle?.[0] || '';
    }
  }

  const footerComp = findComp('FOOTER');
  if (footerComp) {
    form.footer.enabled = true;
    form.footer.text = footerComp.text || '';
  }

  const buttonsComp = findComp('BUTTONS');
  if (buttonsComp) {
    form.buttons = (buttonsComp.buttons || []).map(b => ({
      type: (b.type || 'QUICK_REPLY').toUpperCase(),
      text: b.text || '',
      url: b.url || '',
      phone_number: b.phone_number || '',
      urlExample: Array.isArray(b.example)
        ? b.example[0] || ''
        : b.example || '',
    }));
  }
};

// ─── edit gating (Meta rules) ────────────────────────────────────────────────────
const EDITABLE_STATUSES = ['approved', 'rejected', 'paused'];
const unsupportedForEdit = computed(() =>
  props.template ? isUnsupportedComponents(props.template.components) : false
);
const editDisabledReason = computed(() => {
  if (!props.template) return '';
  if (unsupportedForEdit.value)
    return 'Este template usa componentes que ainda não podem ser editados por aqui. Edite diretamente na Meta.';
  if (!EDITABLE_STATUSES.includes(templateStatus.value))
    return 'Apenas templates aprovados, rejeitados ou pausados podem ser editados.';
  return '';
});
const canEdit = computed(() => !!props.template && !editDisabledReason.value);

const startEdit = () => {
  currentMode.value = 'edit';
};
const cancelEdit = () => {
  hydrateFromTemplate(props.template);
  uploadedMediaPreview.value = null;
  currentMode.value = 'view';
};

// ─── header copy ──────────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  approved: { label: 'Aprovado', cls: 'bg-emerald-100 text-emerald-700' },
  pending: { label: 'Pendente', cls: 'bg-amber-100 text-amber-700' },
  rejected: { label: 'Rejeitado', cls: 'bg-red-100 text-red-700' },
  paused: { label: 'Pausado', cls: 'bg-slate-100 text-slate-600' },
  disabled: { label: 'Desativado', cls: 'bg-red-100 text-red-600' },
  in_appeal: { label: 'Em Recurso', cls: 'bg-orange-100 text-orange-700' },
};
const statusBadge = computed(
  () =>
    STATUS_CONFIG[templateStatus.value] || {
      label: props.template?.status,
      cls: 'bg-slate-100 text-slate-500',
    }
);
const modalTitle = computed(() => {
  if (isCreate.value) return 'Criar Template WhatsApp';
  if (isEdit.value) return 'Editar Template';
  return 'Visualizar Template';
});
const modalSubtitle = computed(() => {
  if (isCreate.value)
    return 'O template será enviado para aprovação da Meta antes de poder ser usado';
  if (isEdit.value)
    return 'Ao salvar, o template volta para revisão da Meta (nome e idioma não podem mudar)';
  return 'Pré-visualização do template como aparece no WhatsApp';
});

if (props.template) hydrateFromTemplate(props.template);

// ─── validation ─────────────────────────────────────────────────────────────────
const validate = () => {
  if (!selectedInboxId.value) return 'Selecione uma caixa de entrada';
  if (!form.name) return 'Nome é obrigatório';
  if (!/^[a-z0-9_]+$/.test(form.name))
    return 'Nome: apenas letras minúsculas, números e _';
  if (!form.body.text) return 'Corpo é obrigatório';
  for (const n of bodyVars.value) {
    if (!form.body.examples[n])
      return `Informe o exemplo para a variável {{${n}}}`;
  }
  if (form.header.enabled && form.header.type === 'TEXT' && !form.header.text)
    return 'Texto do cabeçalho é obrigatório';
  if (
    form.header.enabled &&
    ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(form.header.type) &&
    !form.header.url
  )
    return 'Faça upload ou informe o ID/URL da mídia do cabeçalho';
  if (headerHasVar.value && !form.header.example)
    return 'Informe o exemplo para {{1}} no cabeçalho';
  for (const [i, btn] of form.buttons.entries()) {
    if (!btn.text) return `Botão ${i + 1}: texto é obrigatório`;
    if (btn.type === 'URL' && !btn.url)
      return `Botão ${i + 1}: URL é obrigatório`;
    if (btn.type === 'PHONE_NUMBER' && !btn.phone_number)
      return `Botão ${i + 1}: número de telefone é obrigatório`;
    if (buttonHasUrlVar(btn) && !btn.urlExample)
      return `Botão ${i + 1}: informe o exemplo para {{1}} na URL`;
  }
  return null;
};

// ─── submit ─────────────────────────────────────────────────────────────────────
const submit = async () => {
  const err = validate();
  if (err) {
    useAlert(err);
    return;
  }

  submitting.value = true;
  try {
    if (isEdit.value) {
      const payload = { components: buildComponents() };
      if (!categoryLocked.value) payload.category = form.category;
      const res = await WhatsappCampaignsAPI.updateTemplate(
        selectedInboxId.value,
        props.template.id,
        payload
      );
      emit('saved', res.data);
    } else {
      const res = await WhatsappCampaignsAPI.createTemplate(
        selectedInboxId.value,
        {
          name: form.name,
          language: form.language,
          category: form.category,
          components: buildComponents(),
        }
      );
      emit('created', res.data);
    }
  } catch (e) {
    useAlert(
      e?.response?.data?.error ||
        (isEdit.value ? 'Erro ao salvar template' : 'Erro ao criar template')
    );
  } finally {
    submitting.value = false;
  }
};

// ─── media upload ────────────────────────────────────────────────────────────────
const ACCEPTED_TYPES = {
  IMAGE: 'image/jpeg,image/png,image/webp',
  VIDEO: 'video/mp4,video/3gpp',
  DOCUMENT:
    'application/pdf,application/vnd.ms-powerpoint,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain',
};

const triggerMediaUpload = () => {
  if (mediaFileInput.value) mediaFileInput.value.click();
};

const onMediaFileChange = async event => {
  const file = event.target.files[0];
  if (!file) return;

  if (!selectedInboxId.value) {
    useAlert('Selecione uma caixa de entrada antes de fazer upload');
    return;
  }

  uploadingMedia.value = true;
  uploadedMediaPreview.value = null;

  try {
    const formData = new FormData();
    formData.append('file', file);

    const res = await WhatsappCampaignsAPI.uploadTemplateMedia(
      selectedInboxId.value,
      formData
    );
    form.header.url = res.data.media_id;

    // local preview URL (only for images)
    if (form.header.type === 'IMAGE') {
      uploadedMediaPreview.value = URL.createObjectURL(file);
    } else {
      uploadedMediaPreview.value = file.name;
    }

    useAlert('Mídia enviada com sucesso!');
  } catch (e) {
    useAlert(e?.response?.data?.error || 'Erro ao fazer upload da mídia');
  } finally {
    uploadingMedia.value = false;
    // reset so the same file can be re-selected
    event.target.value = '';
  }
};

const clearMedia = () => {
  form.header.url = '';
  uploadedMediaPreview.value = null;
};

// ─── section label helpers ──────────────────────────────────────────────────────
const varLabel = n => `{{${n}}}`;

const mediaIcon = {
  IMAGE: 'i-lucide-image',
  VIDEO: 'i-lucide-video',
  DOCUMENT: 'i-lucide-file',
};
const mediaLabel = { IMAGE: 'Imagem', VIDEO: 'Vídeo', DOCUMENT: 'Documento' };
const btnTypeIcon = {
  QUICK_REPLY: 'i-lucide-reply',
  URL: 'i-lucide-external-link',
  PHONE_NUMBER: 'i-lucide-phone',
};
</script>

<template>
  <!-- Backdrop -->
  <div
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
  >
    <div
      class="flex h-[92vh] w-full max-w-[1100px] flex-col overflow-hidden rounded-2xl bg-n-surface-1 shadow-2xl"
    >
      <!-- ── Header ── -->
      <div
        class="flex items-center justify-between border-b border-n-alpha-3 px-6 py-4"
      >
        <div>
          <div class="flex items-center gap-2">
            <h2 class="text-lg font-semibold text-n-slate-12">
              {{ modalTitle }}
            </h2>
            <span
              v-if="!isCreate && props.template"
              class="rounded-full px-2.5 py-0.5 text-xs font-medium"
              :class="statusBadge.cls"
            >
              {{ statusBadge.label }}
            </span>
          </div>
          <p class="text-xs text-n-slate-9">
            {{ modalSubtitle }}
          </p>
        </div>
        <button
          class="rounded-lg p-2 text-n-slate-9 transition hover:bg-n-alpha-2"
          @click="$emit('close')"
        >
          <span class="i-lucide-x text-lg" />
        </button>
      </div>

      <!-- ── Body: two-column ── -->
      <div class="flex min-h-0 flex-1 overflow-hidden">
        <!-- Left: form -->
        <div class="flex-1 overflow-y-auto p-6">
          <fieldset
            :disabled="readOnly"
            class="m-0 min-w-0 space-y-5 border-0 p-0 disabled:opacity-90"
          >
            <!-- ─ 1. Informações básicas ─ -->
            <section class="rounded-xl border border-n-alpha-3 p-5">
              <h3 class="mb-4 text-sm font-semibold text-n-slate-12">
                Informações Básicas
              </h3>
              <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <!-- Inbox selector (shown only if not pre-selected) -->
                <div
                  v-if="!props.inboxId"
                  class="flex flex-col gap-1 sm:col-span-2"
                >
                  <label class="text-xs font-medium text-n-slate-11">Caixa de Entrada *</label>
                  <select
                    v-model="selectedInboxId"
                    class="h-9 rounded-lg border border-n-slate-5 bg-n-slate-1 px-3 text-sm outline-none focus:border-n-blue-8"
                  >
                    <option value="">Selecione...</option>
                    <option
                      v-for="inbox in inboxes"
                      :key="inbox.id"
                      :value="inbox.id"
                    >
                      {{ inbox.name }}
                    </option>
                  </select>
                </div>

                <!-- Name -->
                <div class="flex flex-col gap-1 sm:col-span-2">
                  <label class="text-xs font-medium text-n-slate-11">Nome do Template *</label>
                  <input
                    v-model="form.name"
                    :disabled="nameLocked"
                    placeholder="ex: confirmar_consulta"
                    class="h-9 rounded-lg border border-n-slate-5 bg-n-slate-1 px-3 text-sm font-mono outline-none focus:border-n-blue-8 disabled:cursor-not-allowed disabled:opacity-60"
                    @input="
                      form.name = form.name
                        .toLowerCase()
                        .replace(/[^a-z0-9_]/g, '')
                    "
                  />
                  <p class="text-[11px] text-n-slate-9">
                    {{
                      nameLocked
                        ? 'O nome não pode ser alterado após a criação'
                        : 'Apenas letras minúsculas, números e underscore'
                    }}
                  </p>
                </div>

                <!-- Language -->
                <div class="flex flex-col gap-1">
                  <label class="text-xs font-medium text-n-slate-11">Idioma *</label>
                  <select
                    v-model="form.language"
                    :disabled="nameLocked"
                    class="h-9 rounded-lg border border-n-slate-5 bg-n-slate-1 px-3 text-sm outline-none focus:border-n-blue-8 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <option
                      v-for="lang in LANGUAGES"
                      :key="lang.value"
                      :value="lang.value"
                    >
                      {{ lang.label }}
                    </option>
                  </select>
                </div>

                <!-- Category -->
                <div class="flex flex-col gap-1">
                  <label class="text-xs font-medium text-n-slate-11">Categoria *</label>
                  <select
                    v-model="form.category"
                    :disabled="categoryLocked"
                    class="h-9 rounded-lg border border-n-slate-5 bg-n-slate-1 px-3 text-sm outline-none focus:border-n-blue-8 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <option value="MARKETING">Marketing</option>
                    <option value="UTILITY">Utilitário</option>
                    <option value="AUTHENTICATION">Autenticação</option>
                  </select>
                  <p v-if="categoryLocked" class="text-[11px] text-n-slate-9">
                    A categoria não pode ser alterada em templates aprovados
                  </p>
                </div>
              </div>
            </section>

            <!-- ─ 2. Cabeçalho ─ -->
            <section class="rounded-xl border border-n-alpha-3 p-5">
              <div class="mb-4 flex items-center justify-between">
                <h3 class="text-sm font-semibold text-n-slate-12">Cabeçalho</h3>
                <label
                  class="flex cursor-pointer items-center gap-2 text-xs text-n-slate-10"
                >
                  <input
                    v-model="form.header.enabled"
                    type="checkbox"
                    class="rounded"
                  />
                  Ativar cabeçalho
                </label>
              </div>

              <div v-if="form.header.enabled" class="space-y-3">
                <!-- Type picker -->
                <div class="flex gap-2">
                  <button
                    v-for="type in ['TEXT', 'IMAGE', 'VIDEO', 'DOCUMENT']"
                    :key="type"
                    class="rounded-lg border px-3 py-1.5 text-xs font-medium transition"
                    :class="
                      form.header.type === type
                        ? 'border-n-brand bg-n-brand/10 text-n-brand'
                        : 'border-n-alpha-3 text-n-slate-10 hover:bg-n-alpha-1'
                    "
                    @click="form.header.type = type"
                  >
                    {{
                      type === 'TEXT'
                        ? 'Texto'
                        : type === 'IMAGE'
                          ? 'Imagem'
                          : type === 'VIDEO'
                            ? 'Vídeo'
                            : 'Documento'
                    }}
                  </button>
                </div>

                <!-- TEXT header -->
                <div v-if="form.header.type === 'TEXT'" class="space-y-2">
                  <input
                    v-model="form.header.text"
                    placeholder="Texto do cabeçalho (máx. 60 chars, use {{1}} para variável)"
                    maxlength="60"
                    class="h-9 w-full rounded-lg border border-n-slate-5 bg-n-slate-1 px-3 text-sm outline-none focus:border-n-blue-8"
                  />
                  <input
                    v-if="headerHasVar"
                    v-model="form.header.example"
                    placeholder="Exemplo para {{1}}"
                    class="h-9 w-full rounded-lg border border-amber-300 bg-amber-50 px-3 text-sm outline-none focus:border-amber-500"
                  />
                </div>

                <!-- Media header -->
                <div v-else class="space-y-3">
                  <!-- Upload button area -->
                  <div
                    class="rounded-lg border border-dashed border-n-alpha-4 bg-n-surface-2 p-4"
                  >
                    <input
                      ref="mediaFileInput"
                      type="file"
                      class="hidden"
                      :accept="ACCEPTED_TYPES[form.header.type]"
                      @change="onMediaFileChange"
                    />

                    <!-- Uploaded preview -->
                    <div v-if="uploadedMediaPreview" class="mb-3">
                      <div
                        v-if="form.header.type === 'IMAGE'"
                        class="flex items-start gap-3"
                      >
                        <img
                          :src="uploadedMediaPreview"
                          class="h-16 w-16 rounded-lg object-cover border border-n-alpha-3"
                          alt="preview"
                        />
                        <div class="flex-1">
                          <p class="text-xs font-medium text-emerald-600">
                            Imagem enviada
                          </p>
                          <p
                            class="text-[11px] text-n-slate-9 font-mono break-all mt-0.5"
                          >
                            ID: {{ form.header.url }}
                          </p>
                        </div>
                        <button
                          class="rounded p-1 text-n-slate-9 hover:text-red-500"
                          @click="clearMedia"
                        >
                          <span class="i-lucide-x text-sm" />
                        </button>
                      </div>
                      <div v-else class="flex items-center gap-3">
                        <span
                          :class="mediaIcon[form.header.type]"
                          class="text-2xl text-n-slate-10"
                        />
                        <div class="flex-1">
                          <p class="text-xs font-medium text-emerald-600">
                            Arquivo enviado
                          </p>
                          <p class="text-[11px] text-n-slate-9 break-all">
                            {{ uploadedMediaPreview }}
                          </p>
                        </div>
                        <button
                          class="rounded p-1 text-n-slate-9 hover:text-red-500"
                          @click="clearMedia"
                        >
                          <span class="i-lucide-x text-sm" />
                        </button>
                      </div>
                    </div>

                    <!-- Upload button -->
                    <button
                      class="flex w-full items-center justify-center gap-2 rounded-lg border border-n-alpha-3 bg-n-surface-1 px-4 py-2.5 text-sm font-medium text-n-slate-11 transition hover:bg-n-alpha-1 disabled:opacity-50"
                      :disabled="uploadingMedia"
                      @click="triggerMediaUpload"
                    >
                      <span
                        v-if="uploadingMedia"
                        class="i-lucide-loader-circle animate-spin text-base"
                      />
                      <span
                        v-else
                        :class="mediaIcon[form.header.type]"
                        class="text-base"
                      />
                      <span v-if="uploadingMedia">Enviando...</span>
                      <span v-else-if="uploadedMediaPreview">Substituir {{ mediaLabel[form.header.type] }}</span>
                      <span v-else>Fazer upload de
                        {{ mediaLabel[form.header.type] }}</span>
                    </button>

                    <p class="mt-2 text-center text-[11px] text-n-slate-9">
                      <span v-if="form.header.type === 'IMAGE'">JPG, PNG ou WebP</span>
                      <span v-else-if="form.header.type === 'VIDEO'">MP4 ou 3GPP</span>
                      <span v-else>PDF, Word, Excel, PowerPoint ou TXT</span>
                    </p>
                  </div>

                  <!-- Manual URL fallback -->
                  <details class="group">
                    <summary
                      class="cursor-pointer list-none text-[11px] text-n-slate-9 hover:text-n-slate-11 flex items-center gap-1"
                    >
                      <span
                        class="i-lucide-chevron-right text-xs transition group-open:rotate-90"
                      />
                      Ou informar URL/ID manualmente
                    </summary>
                    <div class="mt-2 space-y-1">
                      <input
                        v-model="form.header.url"
                        :placeholder="`URL ou ID de mídia para ${mediaLabel[form.header.type]}`"
                        class="h-9 w-full rounded-lg border border-n-slate-5 bg-n-slate-1 px-3 text-sm outline-none focus:border-n-blue-8"
                      />
                      <p class="text-[11px] text-n-slate-9">
                        ID retornado pela Meta API ou URL pública da mídia
                      </p>
                    </div>
                  </details>
                </div>
              </div>
            </section>

            <!-- ─ 3. Corpo ─ -->
            <section class="rounded-xl border border-n-alpha-3 p-5">
              <div class="mb-4 flex items-center justify-between">
                <h3 class="text-sm font-semibold text-n-slate-12">Corpo *</h3>
                <span class="text-xs text-n-slate-9">{{ form.body.text.length }}/1024</span>
              </div>

              <textarea
                v-model="form.body.text"
                rows="5"
                maxlength="1024"
                placeholder="Olá {{1}}, sua consulta está confirmada para {{2}}. Aguardamos você!"
                class="w-full resize-none rounded-lg border border-n-slate-5 bg-n-slate-1 px-3 py-2 text-sm outline-none transition focus:border-n-blue-8"
              />

              <p class="mt-1 text-[11px] text-n-slate-9">
                Use *negrito*, _itálico_, ~tachado~. Variáveis:
                &#123;&#123;1&#125;&#125;, &#123;&#123;2&#125;&#125;…
              </p>

              <!-- Variable examples -->
              <div v-if="bodyVars.length > 0" class="mt-3 space-y-2">
                <p class="text-xs font-medium text-amber-600">
                  Exemplos para as variáveis (obrigatório):
                </p>
                <div
                  v-for="n in bodyVars"
                  :key="n"
                  class="flex items-center gap-2"
                >
                  <span
                    class="w-12 rounded bg-amber-100 px-2 py-1 text-center text-xs font-mono font-medium text-amber-700"
                  >
                    {{ varLabel(n) }}
                  </span>
                  <input
                    v-model="form.body.examples[n]"
                    :placeholder="`Exemplo para ${varLabel(n)}`"
                    class="h-8 flex-1 rounded-lg border border-amber-300 bg-amber-50 px-3 text-sm outline-none focus:border-amber-500"
                  />
                </div>
              </div>
            </section>

            <!-- ─ 4. Rodapé ─ -->
            <section class="rounded-xl border border-n-alpha-3 p-5">
              <div class="mb-4 flex items-center justify-between">
                <h3 class="text-sm font-semibold text-n-slate-12">Rodapé</h3>
                <label
                  class="flex cursor-pointer items-center gap-2 text-xs text-n-slate-10"
                >
                  <input
                    v-model="form.footer.enabled"
                    type="checkbox"
                    class="rounded"
                  />
                  Ativar rodapé
                </label>
              </div>
              <div v-if="form.footer.enabled">
                <input
                  v-model="form.footer.text"
                  placeholder="Texto do rodapé (máx. 60 chars)"
                  maxlength="60"
                  class="h-9 w-full rounded-lg border border-n-slate-5 bg-n-slate-1 px-3 text-sm outline-none focus:border-n-blue-8"
                />
              </div>
            </section>

            <!-- ─ 5. Botões ─ -->
            <section class="rounded-xl border border-n-alpha-3 p-5">
              <div class="mb-4 flex items-center justify-between">
                <h3 class="text-sm font-semibold text-n-slate-12">Botões</h3>
                <button
                  v-if="form.buttons.length < 3"
                  class="flex items-center gap-1 rounded-lg border border-n-alpha-3 px-3 py-1.5 text-xs font-medium text-n-slate-11 transition hover:bg-n-alpha-1"
                  @click="addButton"
                >
                  <span class="i-lucide-plus text-sm" /> Adicionar botão
                </button>
              </div>

              <div
                v-if="form.buttons.length === 0"
                class="text-xs text-n-slate-9"
              >
                Até 3 botões (Resposta Rápida, URL ou Telefone)
              </div>

              <div class="space-y-3">
                <div
                  v-for="(btn, idx) in form.buttons"
                  :key="idx"
                  class="rounded-lg border border-n-alpha-3 bg-n-surface-2 p-4"
                >
                  <div class="mb-3 flex items-center justify-between">
                    <span class="text-xs font-medium text-n-slate-11">Botão {{ idx + 1 }}</span>
                    <button
                      class="rounded p-1 text-n-slate-9 hover:text-red-500"
                      @click="removeButton(idx)"
                    >
                      <span class="i-lucide-trash-2 text-sm" />
                    </button>
                  </div>

                  <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <!-- Type -->
                    <div class="flex flex-col gap-1">
                      <label class="text-[11px] text-n-slate-10">Tipo</label>
                      <select
                        v-model="btn.type"
                        class="h-8 rounded-lg border border-n-slate-5 bg-n-slate-1 px-2 text-xs outline-none focus:border-n-blue-8"
                      >
                        <option
                          v-for="bt in BUTTON_TYPES"
                          :key="bt.value"
                          :value="bt.value"
                        >
                          {{ bt.label }}
                        </option>
                      </select>
                    </div>

                    <!-- Text -->
                    <div class="flex flex-col gap-1">
                      <label class="text-[11px] text-n-slate-10">Texto do botão</label>
                      <input
                        v-model="btn.text"
                        placeholder="ex: Confirmar"
                        maxlength="25"
                        class="h-8 rounded-lg border border-n-slate-5 bg-n-slate-1 px-2 text-xs outline-none focus:border-n-blue-8"
                      />
                    </div>

                    <!-- URL fields -->
                    <template v-if="btn.type === 'URL'">
                      <div class="flex flex-col gap-1 sm:col-span-2">
                        <label class="text-[11px] text-n-slate-10">URL (use &#123;&#123;1&#125;&#125; para
                          variável)</label>
                        <input
                          v-model="btn.url"
                          placeholder="https://exemplo.com/pedido/{{1}}"
                          class="h-8 rounded-lg border border-n-slate-5 bg-n-slate-1 px-2 text-xs outline-none focus:border-n-blue-8"
                        />
                      </div>
                      <div
                        v-if="buttonHasUrlVar(btn)"
                        class="flex flex-col gap-1 sm:col-span-2"
                      >
                        <label class="text-[11px] text-amber-600">Exemplo para &#123;&#123;1&#125;&#125; na URL</label>
                        <input
                          v-model="btn.urlExample"
                          placeholder="ex: 12345"
                          class="h-8 rounded-lg border border-amber-300 bg-amber-50 px-2 text-xs outline-none focus:border-amber-500"
                        />
                      </div>
                    </template>

                    <!-- Phone field -->
                    <div
                      v-if="btn.type === 'PHONE_NUMBER'"
                      class="flex flex-col gap-1 sm:col-span-2"
                    >
                      <label class="text-[11px] text-n-slate-10">Número de telefone</label>
                      <input
                        v-model="btn.phone_number"
                        placeholder="+5511999999999"
                        class="h-8 rounded-lg border border-n-slate-5 bg-n-slate-1 px-2 text-xs outline-none focus:border-n-blue-8"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </fieldset>
        </div>

        <!-- Right: WhatsApp preview -->
        <div
          class="w-[300px] shrink-0 overflow-y-auto border-l border-n-alpha-3 bg-n-surface-2 p-5"
        >
          <p class="mb-4 text-center text-xs font-medium text-n-slate-10">
            Pré-visualização
          </p>

          <!-- Phone frame -->
          <div
            class="mx-auto w-[240px] overflow-hidden rounded-[28px] border-4 border-[#1c2b36] bg-[#111b21] shadow-2xl"
          >
            <!-- Status bar -->
            <div
              class="flex items-center justify-between bg-[#111b21] px-4 py-1.5"
            >
              <span class="text-[10px] font-medium text-white">9:41</span>
              <div class="flex items-center gap-1">
                <span class="i-lucide-signal text-[10px] text-white" />
                <span class="i-lucide-wifi text-[10px] text-white" />
                <span class="i-lucide-battery text-[10px] text-white" />
              </div>
            </div>

            <!-- WA app header -->
            <div class="flex items-center gap-2 bg-[#008069] px-3 py-2">
              <span class="i-lucide-arrow-left text-xs text-white" />
              <div
                class="flex h-7 w-7 items-center justify-center rounded-full bg-[#dfe5e7]"
              >
                <span class="i-lucide-building-2 text-[12px] text-[#008069]" />
              </div>
              <div>
                <div class="text-[11px] font-semibold leading-none text-white">
                  Empresa
                </div>
                <div class="text-[9px] text-[#e9edef]/70">Business Account</div>
              </div>
            </div>

            <!-- Chat area -->
            <div class="min-h-[380px] bg-[#efeae2] px-2 py-3">
              <!-- No body yet -->
              <div
                v-if="!form.body.text"
                class="flex h-full items-center justify-center py-8"
              >
                <p class="text-center text-[10px] text-[#8696a0]">
                  Preencha o corpo para ver a pré-visualização
                </p>
              </div>

              <!-- Message bubble -->
              <div v-else class="ml-0 max-w-[90%]">
                <div class="overflow-hidden rounded-lg bg-white shadow-sm">
                  <!-- Header: TEXT -->
                  <div
                    v-if="
                      form.header.enabled &&
                      form.header.type === 'TEXT' &&
                      form.header.text
                    "
                    class="px-3 pt-3 text-[12px] font-semibold text-[#111b21]"
                  >
                    {{ previewHeaderText }}
                  </div>

                  <!-- Header: IMAGE -->
                  <div
                    v-else-if="
                      form.header.enabled && form.header.type === 'IMAGE'
                    "
                    class="flex h-28 items-center justify-center bg-[#cfd8dc]"
                  >
                    <div class="flex flex-col items-center gap-1">
                      <span class="i-lucide-image text-2xl text-[#78909c]" />
                      <span class="text-[10px] text-[#78909c]">Imagem</span>
                    </div>
                  </div>

                  <!-- Header: VIDEO -->
                  <div
                    v-else-if="
                      form.header.enabled && form.header.type === 'VIDEO'
                    "
                    class="flex h-28 items-center justify-center bg-[#b0bec5]"
                  >
                    <div class="flex flex-col items-center gap-1">
                      <span class="i-lucide-video text-2xl text-[#546e7a]" />
                      <span class="text-[10px] text-[#546e7a]">Vídeo</span>
                    </div>
                  </div>

                  <!-- Header: DOCUMENT -->
                  <div
                    v-else-if="
                      form.header.enabled && form.header.type === 'DOCUMENT'
                    "
                    class="flex h-16 items-center gap-2 bg-[#e3f2fd] px-3"
                  >
                    <span class="i-lucide-file text-xl text-[#1976d2]" />
                    <span class="text-[11px] text-[#1976d2]">Documento.pdf</span>
                  </div>

                  <!-- Body text -->
                  <div
                    class="px-3 py-2 text-[12px] leading-relaxed text-[#111b21]"
                    :class="{ 'pt-2': form.header.enabled }"
                    v-html="renderBold(previewBody)"
                  />

                  <!-- Footer -->
                  <div
                    v-if="form.footer.enabled && form.footer.text"
                    class="px-3 pb-1 text-[10px] text-[#8696a0]"
                  >
                    {{ form.footer.text }}
                  </div>

                  <!-- Timestamp -->
                  <div class="flex justify-end px-3 pb-2 pt-0">
                    <span class="text-[10px] text-[#8696a0]">10:30 ✓✓</span>
                  </div>
                </div>

                <!-- Buttons (outside bubble, below) -->
                <div v-if="form.buttons.length > 0" class="mt-0.5 space-y-0.5">
                  <div
                    v-for="(btn, idx) in form.buttons"
                    :key="idx"
                    class="flex items-center justify-center gap-1.5 rounded-lg bg-white px-2 py-2 shadow-sm"
                  >
                    <span
                      :class="btnTypeIcon[btn.type]"
                      class="text-[12px] text-[#00a884]"
                    />
                    <span class="text-[11px] font-medium text-[#00a884]">
                      {{ btn.text || `Botão ${idx + 1}` }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Template info below phone -->
          <div class="mt-4 space-y-1 text-center">
            <p v-if="form.name" class="font-mono text-[11px] text-n-slate-10">
              {{ form.name }}
            </p>
            <p class="text-[11px] text-n-slate-9">
              {{ form.language }} · {{ form.category }}
            </p>
          </div>
        </div>
      </div>

      <!-- ── Footer ── -->
      <div
        class="flex items-center justify-end gap-3 border-t border-n-alpha-3 px-6 py-4"
      >
        <!-- View mode -->
        <template v-if="isView">
          <p
            v-if="editDisabledReason"
            class="mr-auto max-w-md text-[11px] text-n-slate-9"
          >
            {{ editDisabledReason }}
          </p>
          <Button
            label="Fechar"
            variant="ghost"
            size="sm"
            @click="$emit('close')"
          />
          <Button
            v-if="canEdit"
            label="Editar"
            icon="i-lucide-pencil"
            size="sm"
            @click="startEdit"
          />
        </template>

        <!-- Edit mode -->
        <template v-else-if="isEdit">
          <Button
            label="Cancelar"
            variant="ghost"
            size="sm"
            @click="cancelEdit"
          />
          <Button
            label="Salvar alterações"
            icon="i-lucide-save"
            size="sm"
            :is-loading="submitting"
            :disabled="submitting"
            @click="submit"
          />
        </template>

        <!-- Create mode -->
        <template v-else>
          <Button
            label="Cancelar"
            variant="ghost"
            size="sm"
            @click="$emit('close')"
          />
          <Button
            label="Enviar para Aprovação"
            icon="i-lucide-send"
            size="sm"
            :is-loading="submitting"
            :disabled="submitting"
            @click="submit"
          />
        </template>
      </div>
    </div>
  </div>
</template>
